import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Group, Paper, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useHotkeys, useHover } from "@mantine/hooks";
import { IconArrowBackUp } from "@tabler/icons-react";
import { useSegments, type SegmentFilters } from "../api/queries";
import type { Segment } from "../api/types";
import { REVIEW_DEFAULT, segmentQuery, type Filters } from "../state/prefs";
import { usePlayer } from "../state/player";
import { formatDuration } from "../time/format";
import { msToZonedText } from "../time/parse";
import { useZone } from "../time/useZone";
import { Chip } from "../ui/Chip";
import { DateMenu, FilterMenu } from "../ui/FilterMenu";
import { Badge, BadgeLegend } from "../ui/badges";
import { useUndo } from "./undo";
import { FeedbackLine } from "../ui/FeedbackLine";
import { useLayout } from "../ui/useLayout";

/**
 * What the queue asks the server for.
 *
 * Pulled out of the component because the interesting part is a rule rather than a shape: the
 * queue may never offer a row whose boundaries the segmenter is still moving, whatever the
 * filter panel says. Any edit locks the row against re-detection
 * (worker.segmenter._is_untouched), so an admin who renames what is on air, or nudges its end,
 * freezes it at whatever the last pass had written -- and the rest of that show or track is
 * never segmented at all. Hence `onAir: false` here rather than in the panel: it is not a
 * filter, it is the one thing about this list that is not the operator's to change.
 */
export function queueQuery(filters: Filters, zone: string): SegmentFilters {
  return { ...segmentQuery(filters, zone), onAir: false, limit: 100 };
}

/**
 * The review queue.
 *
 * It exists because retention will approve anything still provisional six hours before its
 * audio expires, using the detected boundaries as they stand. Everything here is something a
 * person still has time to correct.
 */
export function Queue() {
  const error = useUndo((s) => s.error);
  const [filters, setFilters] = useState<Filters>(REVIEW_DEFAULT);
  const selectedId = usePlayer((s) => s.selectedId);
  const zone = useZone();
  const { t } = useTranslation("admin");

  const { data, isFetching } = useSegments(queueQuery(filters, zone.name));
  const items = data?.items ?? [];
  // Review mode has no band of its own to hang this off -- it is the archive page with the
  // queue and the inspector swapped in -- and the queue is the one panel that is always on
  // screen while edits are being made, so undo lives in its head.
  useUndoHotkey();

  return (
    <Paper
      component="section"
      p="sm"
      aria-label={t("queue.regionLabel")}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* `gap` and `wrap` spelled out rather than left to Mantine's defaults: this row is
          deliberately a single non-wrapping line, and the 8px `mb` is the rhythm the archive's
          head shares. */}
      <Group gap={8} mb={8} wrap="nowrap">
        <DateMenu value={filters} onChange={setFilters} />
        <FilterMenu value={filters} onChange={setFilters} review />
        <UndoButton />
        {/* Outside the panel: it changes nothing about what the list shows. */}
        <BadgeLegend of={["auto", "unlinked", "starred", "expired"]} />
      </Group>
      <FeedbackLine role="alert" mb={8}>
        {error}
      </FeedbackLine>

      <Box
        component="ul"
        m={0}
        p={0}
        flex={1}
        mih={0}
        style={{ listStyle: "none", overflowY: "auto", background: "transparent" }}
      >
        {items.map((segment, index) => (
          <QueueRow
            key={segment.id}
            segment={segment}
            selected={segment.id === selectedId}
            first={index === 0}
          />
        ))}
        {!items.length &&
          (isFetching ? (
            // Four rows of the height a real one settles at, so the panel does not jump when
            // the list arrives. `component="li"` because the parent is a `<ul>`.
            [0, 1, 2, 3].map((i) => <Skeleton key={i} component="li" height={34} mb={1} />)
          ) : (
            <Text component="li" p={22} ta="center" c="dimmed">
              {t("queue.nothingWaiting")}
            </Text>
          ))}
      </Box>
    </Paper>
  );
}

function QueueRow({
  segment,
  selected,
  first,
}: {
  segment: Segment;
  selected: boolean;
  /** First row in the list: the rule between rows sits on every row but this one. */
  first: boolean;
}) {
  const zone = useZone();
  const { t } = useTranslation("admin");
  const { phone } = useLayout();
  const startMs = Date.parse(segment.start);
  // The one declaration on this row that no Mantine prop carries: `UnstyledButton` has no
  // hover of its own, and a `style` object cannot hold a pseudo-class. `useHover` is
  // @mantine/hooks' own answer -- two listeners and a boolean, read straight into `bg`.
  const { hovered, ref } = useHover<HTMLButtonElement>();

  return (
    <Box
      component="li"
      style={first ? undefined : { borderTop: "1px solid var(--mantine-color-default-border)" }}
    >
      <UnstyledButton
        ref={ref}
        w="100%"
        px={12}
        py={7}
        ta="left"
        bg={selected ? "accentDim" : hovered ? "var(--mantine-color-default-hover)" : undefined}
        style={{
          // Flags sit left of the length, and both last tracks are content-sized: the number
          // ends on the row's own padding, and the badges are one gap from it rather than one
          // gap plus whatever a fixed track had left over.
          display: "grid",
          gridTemplateColumns: phone
            ? "minmax(0, 1fr) auto auto"
            : "160px minmax(0, 1fr) auto auto",
          gap: phone ? "4px 8px" : 12,
          alignItems: "center",
        }}
        onClick={() => {
          const player = usePlayer.getState();
          player.select(segment.id);
          // The same gesture as a row in the archive list: the row you pick is the row you
          // hear, and it is what the player panel names. This queue used to only nudge the
          // playhead, which the audio's own `timeupdate` overwrote within a couple of hundred
          // milliseconds -- so the panel flashed onto the row and straight back, and with
          // nothing playing it never named the row at all.
          //
          // A tombstoned row is handed over like any other. The player's own loader is what
          // refuses it and says why, and routing it there is what makes the pane name this
          // row rather than whatever played last.
          player.play({ kind: "segment", segment });
          // Pins the view to the playhead, which `play` has just put on this row: that is what
          // carries the timeline to it.
          player.setFollow(true);
        }}
      >
        <Text
          component="span"
          size="xs"
          c="dimmed"
          style={{ fontVariantNumeric: "tabular-nums", gridColumn: phone ? "1 / -1" : undefined }}
        >
          {msToZonedText(startMs, zone.name)}
        </Text>
        <Text
          component="span"
          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {segment.title ?? segment.raw_title ?? t("queue.untitled")}
        </Text>
        {/* One letter each, colour-coded: three spelled-out words per row pushed the title
            out of the way, and the row's job is to be scanned. The `title` is the long form. */}
        <Box component="span" display="flex" style={{ gap: 5 }}>
          {segment.auto_approved && <Badge of="auto" />}
          {segment.event_id === null && segment.kind === "show" && <Badge of="unlinked" />}
          {segment.starred && <Badge of="starred" />}
          {!segment.playable && <Badge of="expired" />}
        </Box>
        <Text component="span" size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatDuration(segment.seconds)}
        </Text>
      </UnstyledButton>
    </Box>
  );
}

function UndoButton() {
  const { t } = useTranslation("admin");
  const entries = useUndo((s) => s.entries);
  const busy = useUndo((s) => s.busy);
  const last = entries[entries.length - 1];

  return (
    // `ml="auto"`: the pair travels at the right end of the queue head -- Undo, then the key.
    <Group gap={8} ml="auto" wrap="nowrap">
      <Chip
        disabled={!last || busy}
        leftSection={<IconArrowBackUp size={15} stroke={1.5} aria-hidden="true" />}
        title={last ? t("queue.undoTitle", { label: last.label }) : t("queue.nothingToUndo")}
        onClick={() => void useUndo.getState().undo(t)}
      >
        {busy
          ? t("queue.undoing")
          : last
            ? t("queue.undoDepth", { depth: entries.length })
            : t("queue.undo")}
      </Chip>
    </Group>
  );
}

function useUndoHotkey(): void {
  const { t } = useTranslation("admin");
  // Mantine's defaults are the whole guard, which is why no options element is passed: it is
  // the omission that leaves `preventDefault` on, and `tagsToIgnore` already spares the text
  // fields where Ctrl+Z is the field's own undo and belongs to the browser. Two deliberate
  // changes ride along -- a focused `<select>` is spared as well now, and `Ctrl+Shift+Z` no
  // longer reaches this, because the match is exact and that chord is redo.
  useHotkeys([["mod+Z", () => void useUndo.getState().undo(t)]]);
}
